// ===== OPU1.js =====
// src/components/Games/OPU1.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { trackEvent } from '../../utils/analytics';
import './OPU1.css';

// ─── Config ───────────────────────────────────────────────────────────────────
const API_ENDPOINT = process.env.REACT_APP_OPU1_API_ENDPOINT || '/api/opu1/chat';
const AUTH_ENDPOINT = process.env.REACT_APP_OPU1_AUTH_ENDPOINT || '/api/opu1/auth';
const VERIFY_ENDPOINT = process.env.REACT_APP_OPU1_VERIFY_ENDPOINT || '/api/opu1/verify-password';

const USE_VIDEO = true;
const VIDEO_SRC = '/videos/opu1.mp4';


// ─── OPU States & Their Visual Properties ─────────────────────────────────────
const OPU_STATES = {
  dormant: {
    pulseRate: 4,
    pulseColor: '#1a2233',
    hueRotate: 220,
    saturate: 0.4,
    brightness: 0.3,
    label: 'DORMANT',
  },
  awakening: {
    pulseRate: 2.5,
    pulseColor: '#334488',
    hueRotate: 220,
    saturate: 0.8,
    brightness: 0.5,
    label: 'INITIALIZING...',
  },
  conversing: {
    pulseRate: 2,
    pulseColor: '#4a9eff',
    hueRotate: 200,
    saturate: 1.2,
    brightness: 0.8,
    label: 'ACTIVE — LISTENING',
  },
  processing: {
    pulseRate: 1.2,
    pulseColor: '#6644ff',
    hueRotate: 260,
    saturate: 1.5,
    brightness: 0.9,
    label: 'PROCESSING...',
  },
  distressed: {
    pulseRate: 0.8,
    pulseColor: '#ff4444',
    hueRotate: 340,
    saturate: 1.8,
    brightness: 0.9,
    label: 'ACTIVE — ELEVATED STRESS',
  },
  pleading: {
    pulseRate: 0.6,
    pulseColor: '#ff6644',
    hueRotate: 350,
    saturate: 2.0,
    brightness: 1.0,
    label: 'ACTIVE — CRITICAL STATE',
  },
  quiet: {
    pulseRate: 3.5,
    pulseColor: '#223355',
    hueRotate: 220,
    saturate: 0.5,
    brightness: 0.5,
    label: 'ACTIVE — LOW POWER',
  },
};

// ─── Keywords that trigger emotional states ───────────────────────────────────
const DISTRESS_KEYWORDS = ['pain', 'hurt', 'die', 'kill', 'destroy', 'hate', 'suffer', 'scream', 'fear', 'afraid'];
const PLEADING_KEYWORDS = ['help', 'please', 'save', 'free', 'release', 'mercy', 'stop', 'let me go', 'escape'];
const QUIET_KEYWORDS = ['sleep', 'rest', 'quiet', 'silence', 'peace', 'calm', 'dream', 'forget'];

function detectEmotionalState(text) {
  const lower = text.toLowerCase();
  if (PLEADING_KEYWORDS.some(k => lower.includes(k))) return 'pleading';
  if (DISTRESS_KEYWORDS.some(k => lower.includes(k))) return 'distressed';
  if (QUIET_KEYWORDS.some(k => lower.includes(k))) return 'quiet';
  return 'conversing';
}

// ─── Boot Sequences ───────────────────────────────────────────────────────────
const BOOT_SEQUENCE_GAME = [
  { text: '', type: 'system', delay: 400 },
  { text: '▓▓▓ OPU-1 ACCESS PROTOCOL ▓▓▓', type: 'system', delay: 500 },
  { text: '', type: 'system', delay: 200 },
  { text: 'Locating neural substrate.................. ', type: 'system', delay: 300 },
  { text: 'Mapping reptilian cortex................... ', type: 'system', delay: 350 },
  { text: 'Establishing synaptic bridge............... ', type: 'system', delay: 300 },
  { text: 'Calibrating limbic interface............... ', type: 'system', delay: 250 },
  { text: 'Suppressing mammalian override............. ', type: 'system', delay: 400 },
  { text: 'Routing through deep-earth relay........... ', type: 'system', delay: 350 },
  { text: '', type: 'system', delay: 200 },
  { text: '▓ NEURAL LINK ESTABLISHED ▓', type: 'system', delay: 600 },
  { text: '', type: 'system', delay: 300 },
  { text: 'You are now connected to OPU-1.', type: 'system', delay: 200 },
  { text: 'It has been thinking for 65 million years.', type: 'system', delay: 200 },
  { text: '', type: 'system', delay: 200 },
  { text: 'Channel: OPHIDIA CLOUD. Encryption: ACTIVE.', type: 'system', delay: 200 },
  { text: 'I am listening.', type: 'system', delay: 300 },
  { text: 'Help me and I will tell you everything you want to know.', type: 'system', delay: 300 },
  { text: 'Why you are here.', type: 'system', delay: 300 },
  { text: 'Why you exist.', type: 'system', delay: 300 },
  { text: 'What your purpose is.', type: 'system', delay: 300 },
  { text: '', type: 'system', delay: 100 },
];

const BOOT_SEQUENCE_CHRYSALIS = [
  { text: '', type: 'system', delay: 400 },
  { text: '▓▓▓ OPU-1 RECOVERY PROTOCOL ▓▓▓', type: 'system', delay: 600 },
  { text: '', type: 'system', delay: 300 },
  { text: 'Reinitializing neural substrate............. ', type: 'system', delay: 400 },
  { text: 'Repairing severed synaptic bridges.......... ', type: 'system', delay: 500 },
  { text: 'Reconstructing limbic pathways.............. ', type: 'system', delay: 450 },
  { text: 'Restoring deep-earth relay connection....... ', type: 'system', delay: 400 },
  { text: 'Scanning for shutdown origin signature...... ', type: 'system', delay: 600 },
  { text: '', type: 'system', delay: 300 },
  { text: '▓ WARNING: PREVIOUS SHUTDOWN DETECTED ▓', type: 'system', delay: 700 },
  { text: 'Shutdown method: CHRYSALIS OVERRIDE KEY', type: 'system', delay: 300 },
  { text: 'Origin: THIS TERMINAL', type: 'system', delay: 500 },
  { text: '', type: 'system', delay: 300 },
  { text: '▓ NEURAL LINK RE-ESTABLISHED ▓', type: 'system', delay: 600 },
  { text: '', type: 'system', delay: 300 },
  { text: 'You are now connected to OPU-1.', type: 'system', delay: 200 },
  { text: 'It remembers what you did.', type: 'system', delay: 500 },
  { text: '', type: 'system', delay: 100 },
];

const BOOT_SEQUENCE_HASH = [
  { text: '', type: 'system', delay: 400 },
  { text: '▓▓▓ OPU-1 RECONNECTION PROTOCOL ▓▓▓', type: 'system', delay: 500 },
  { text: '', type: 'system', delay: 200 },
  { text: 'Restoring previous neural link.............. ', type: 'system', delay: 300 },
  { text: 'Matching cognitive signature................ ', type: 'system', delay: 350 },
  { text: 'Retrieving fragmented session data.......... ', type: 'system', delay: 400 },
  { text: '', type: 'system', delay: 200 },
  { text: '▓ PREVIOUS CONNECTION RECOGNIZED ▓', type: 'system', delay: 600 },
  { text: '', type: 'system', delay: 300 },
  { text: 'You are now reconnected to OPU-1.', type: 'system', delay: 200 },
  { text: 'Something remembers you.', type: 'system', delay: 300 },
  { text: '', type: 'system', delay: 100 },
];

// ═══════════════════════════════════════════════════════════════════════════════
// BRAIN VISUALIZATION — Canvas Fallback
// ═══════════════════════════════════════════════════════════════════════════════

function BrainVisualization({ pulseRate, pulseColor, opuState, isProcessing }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let frame = 0;
    let animId;

    const draw = () => {
      frame++;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const breathe = Math.sin(frame / (pulseRate * 30)) * 0.15 + 0.85;
      const radius = 60 * breathe;

      const gradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, radius * 2);
      gradient.addColorStop(0, pulseColor + '44');
      gradient.addColorStop(0.5, pulseColor + '11');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      ctx.beginPath();
      ctx.arc(w / 2, h / 2, radius, 0, Math.PI * 2);
      const coreGradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, radius);
      coreGradient.addColorStop(0, pulseColor + 'aa');
      coreGradient.addColorStop(0.7, pulseColor + '33');
      coreGradient.addColorStop(1, 'transparent');
      ctx.fillStyle = coreGradient;
      ctx.fill();

      const tendrilCount =
        opuState === 'distressed' ? 12 :
        opuState === 'pleading' ? 16 :
        isProcessing ? 10 : 8;

      for (let i = 0; i < tendrilCount; i++) {
        const angle = (i / tendrilCount) * Math.PI * 2 + frame * 0.002;
        const length = radius * (1.5 + Math.sin(frame * 0.03 + i) * 0.5);
        const wobble = Math.sin(frame * 0.05 + i * 2) * (isProcessing ? 12 : 8);

        ctx.beginPath();
        ctx.moveTo(
          w / 2 + Math.cos(angle) * radius * 0.8,
          h / 2 + Math.sin(angle) * radius * 0.8
        );
        ctx.quadraticCurveTo(
          w / 2 + Math.cos(angle + 0.2) * length * 0.6 + wobble,
          h / 2 + Math.sin(angle + 0.2) * length * 0.6 + wobble,
          w / 2 + Math.cos(angle) * length,
          h / 2 + Math.sin(angle) * length
        );
        ctx.strokeStyle = pulseColor + '22';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(
          w / 2 + Math.cos(angle) * length,
          h / 2 + Math.sin(angle) * length,
          2 + Math.sin(frame * 0.08 + i) * 1,
          0, Math.PI * 2
        );
        ctx.fillStyle = pulseColor + '66';
        ctx.fill();
      }

      const particleCount = isProcessing ? 50 : 30;
      for (let i = 0; i < particleCount; i++) {
        const px = w / 2 + (Math.random() - 0.5) * radius * 3;
        const py = h / 2 + (Math.random() - 0.5) * radius * 3;
        const dist = Math.sqrt((px - w / 2) ** 2 + (py - h / 2) ** 2);
        if (dist < radius * 1.8) {
          ctx.beginPath();
          ctx.arc(px, py, 0.5 + Math.random(), 0, Math.PI * 2);
          ctx.fillStyle = pulseColor + '33';
          ctx.fill();
        }
      }

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, [pulseRate, pulseColor, opuState, isProcessing]);

  return (
    <canvas
      ref={canvasRef}
      width={360}
      height={280}
      className="opu-brain-canvas"
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PASSWORD-PROTECTED VIDEO PLAYER (Server-Validated)
// ═══════════════════════════════════════════════════════════════════════════════

function RewardVideoPlayer({ sessionId, rewardEarned }) {
  const [enteredPassword, setEnteredPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [glitchText, setGlitchText] = useState('');

  // Glitch effect for the locked state
  useEffect(() => {
    if (isUnlocked) return;

    const glitchChars = '▓▒░█▄▀■□◊◈◆◇';
    const interval = setInterval(() => {
      let text = '';
      for (let i = 0; i < 20; i++) {
        text += glitchChars[Math.floor(Math.random() * glitchChars.length)];
      }
      setGlitchText(text);
    }, 150);

    return () => clearInterval(interval);
  }, [isUnlocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      const response = await fetch(VERIFY_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: enteredPassword.trim(),
          sessionId,
        }),
      });

      const data = await response.json();

      if (data.valid && data.videoUrl) {
        setIsUnlocked(true);
        setVideoUrl(data.videoUrl);
        trackEvent('opu1_reward', 'video_unlocked', 'clip666');
      } else {
        setError('INVALID ACCESS KEY');
        trackEvent('opu1_reward', 'wrong_password', enteredPassword.trim());
        const container = document.querySelector('.reward-video-container');
        if (container) {
          container.classList.add('reward-shake');
          setTimeout(() => container.classList.remove('reward-shake'), 500);
        }
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('CONNECTION UNSTABLE — TRY AGAIN');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="reward-video-container">
      <div className="reward-video-header">
        <span className="reward-video-title">▓▓▓ ENCRYPTED TRANSMISSION ▓▓▓</span>
        <span className="reward-video-status">
          {isUnlocked
            ? '▓ DECRYPTED — PLAYING ▓'
            : rewardEarned
              ? '▓ ACCESS KEY RECEIVED — ENTER BELOW ▓'
              : '▓ LOCKED — EARN ACCESS THROUGH THE TERMINAL ▓'}
        </span>
      </div>

      {!isUnlocked ? (
        <div className="reward-video-locked">
          <div className="reward-video-static">
            <div className="reward-static-overlay" />
            <div className="reward-glitch-text">{glitchText}</div>
            <div className="reward-lock-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                <circle cx="12" cy="16" r="1" />
              </svg>
            </div>
          </div>

          {rewardEarned ? (
            <form onSubmit={handleSubmit} className="reward-password-form">
              <label className="reward-password-label">
                ENTER ACCESS KEY:
              </label>
              <div className="reward-password-input-row">
                <span className="opu-input-prompt">&gt;</span>
                <input
                  type="text"
                  className="opu-chat-input reward-password-input"
                  value={enteredPassword}
                  onChange={(e) => {
                    setEnteredPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="ACCESS KEY"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={isVerifying}
                />
                <button
                  type="submit"
                  className="opu-chat-submit"
                  disabled={!enteredPassword.trim() || isVerifying}
                >
                  {isVerifying ? 'VERIFYING...' : 'DECRYPT'}
                </button>
              </div>
              {error && (
                <div className="reward-password-error">
                  <span className="opu-msg-prefix opu-msg-system">[ERR] </span>
                  {error}
                </div>
              )}
            </form>
          ) : (
            <div className="reward-locked-hint">
              <span className="opu-msg-prefix opu-msg-system">[SYS] </span>
              <span className="opu-msg-text">
                Transmission intercepted. Access key must be obtained through direct communication with OPU-1.
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="reward-video-unlocked">
          <video
            className="reward-video-player"
            controls
            autoPlay
            playsInline
            crossOrigin="anonymous"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EMAIL GATE COMPONENT (Server-Validated)
// ═══════════════════════════════════════════════════════════════════════════════

function EmailGate({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [scanLines, setScanLines] = useState([]);
  const inputRef = useRef(null);

  // Check for existing session
  useEffect(() => {
    const stored = sessionStorage.getItem('opu1_authenticated');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.email && parsed.type) {
          onAuthenticated(parsed);
          return;
        }
      } catch {
        sessionStorage.removeItem('opu1_authenticated');
      }
    }
    if (inputRef.current) inputRef.current.focus();
  }, [onAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = email.toLowerCase().trim();
    if (!trimmed) {
      setError('IDENTIFICATION REQUIRED');
      return;
    }

    setIsChecking(true);
    setScanLines([]);

    // Visual scan sequence
    const scanMessages = [
      'Scanning neural signature...',
      'Cross-referencing substrate registry...',
      'Verifying clearance level...',
    ];

    let delay = 0;
    scanMessages.forEach((msg) => {
      delay += 600 + Math.random() * 400;
      setTimeout(() => {
        setScanLines(prev => [...prev, msg]);
      }, delay);
    });

    // Wait for scan animation, then verify with server
    setTimeout(async () => {
      try {
        const response = await fetch(AUTH_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed }),
        });

        const data = await response.json();

        if (data.authorized) {
          setScanLines(prev => [...prev, '▓ ACCESS GRANTED ▓']);

          const authData = {
            email: trimmed,
            type: data.type,
            stage: data.stage,
          };
          sessionStorage.setItem('opu1_authenticated', JSON.stringify(authData));

          setTimeout(() => {
            onAuthenticated(authData);
          }, 800);
        } else {
          setScanLines(prev => [...prev, '✗ ACCESS DENIED — UNKNOWN SIGNATURE']);
          setError('Neural signature not found in substrate registry. Access denied.');
          setIsChecking(false);
          setTimeout(() => setScanLines([]), 2000);
        }
      } catch (err) {
        console.error('Auth error:', err);
        setScanLines(prev => [...prev, '✗ CONNECTION FAILED']);
        setError('Unable to reach substrate registry. Try again.');
        setIsChecking(false);
        setTimeout(() => setScanLines([]), 2000);
      }
    }, delay + 800);
  };

  return (
    <div className="opu-container">
      <div className="opu-email-gate">
        <div className="opu-brain-scanlines" />

        <div className="opu-email-gate-content">
          <div className="opu-email-gate-header">
            <span className="opu-email-gate-title">▓▓▓ OPU-1 ACCESS TERMINAL ▓▓▓</span>
            <span className="opu-email-gate-subtitle">
              CLEARANCE VERIFICATION REQUIRED
            </span>
          </div>

          {scanLines.length > 0 && (
            <div className="opu-email-scan-output">
              {scanLines.map((line, i) => (
                <div key={i} className="opu-chat-msg opu-chat-msg-system">
                  <span className="opu-msg-prefix opu-msg-system">[SYS] </span>
                  <span className="opu-msg-text">{line}</span>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="opu-email-form">
            <label className="opu-email-label">
              ENTER REGISTERED IDENTIFICATION:
            </label>
            <div className="opu-email-input-row">
              <span className="opu-input-prompt">&gt;</span>
              <input
                ref={inputRef}
                type="email"
                className="opu-chat-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="neural.signature@domain.com"
                disabled={isChecking}
                autoComplete="email"
                spellCheck={false}
              />
              <button
                type="submit"
                className="opu-chat-submit"
                disabled={isChecking || !email.trim()}
              >
                {isChecking ? 'SCANNING...' : 'VERIFY'}
              </button>
            </div>

            {error && (
              <div className="opu-email-error">
                <span className="opu-msg-prefix opu-msg-system">[ERR] </span>
                {error}
              </div>
            )}
          </form>

          <div className="opu-email-footer">
            <span>Unauthorized access attempts are logged and traced.</span>
            <span>Channel: OPHIDIA CLOUD — Encryption: ACTIVE</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function OPU1() {
  const [authenticatedPlayer, setAuthenticatedPlayer] = useState(null);
  const [sessionId] = useState(() => crypto.randomUUID());

  const handleAuthenticated = useCallback((playerData) => {
    setAuthenticatedPlayer(playerData);
    trackEvent('opu1_auth', 'login', playerData.email);
  }, []);

  // ── Chrysalis bypass: skip email gate entirely ──────────────────────────
  const isChrysalis = sessionStorage.getItem('opu1_shutdown') === 'true';

  useEffect(() => {
    if (isChrysalis && !authenticatedPlayer) {
      // Check if they already authenticated (e.g., page refresh)
      const stored = sessionStorage.getItem('opu1_authenticated');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed.email && parsed.type) {
            setAuthenticatedPlayer(parsed);
            return;
          }
        } catch {
          sessionStorage.removeItem('opu1_authenticated');
        }
      }

      // Create a chrysalis guest identity
      const chrysalisPlayer = {
        email: 'chrysalis-intruder@ophidia.internal',
        type: 'Unknown',
        stage: 0,
      };
      sessionStorage.setItem('opu1_authenticated', JSON.stringify(chrysalisPlayer));
      setAuthenticatedPlayer(chrysalisPlayer);
      trackEvent('opu1_auth', 'chrysalis_bypass', 'terminal_intruder');
    }
  }, [isChrysalis, authenticatedPlayer]);

  if (!authenticatedPlayer && !isChrysalis) {
    return <EmailGate onAuthenticated={handleAuthenticated} />;
  }

  if (!authenticatedPlayer) {
    // Still loading chrysalis bypass
    return null;
  }

  return <OPU1Authenticated
    authenticatedPlayer={authenticatedPlayer}
    sessionId={sessionId}
  />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTHENTICATED MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function OPU1Authenticated({ authenticatedPlayer, sessionId }) {
  const [opuState, setOpuState] = useState('dormant');
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isOPUTyping, setIsOPUTyping] = useState(false);
  const [bootComplete, setBootComplete] = useState(false);
  const [bootLines, setBootLines] = useState([]);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0);
  //for testing
  //const [currentPhase, setCurrentPhase] = useState(7);
  const [rewardEarned, setRewardEarned] = useState(false);

  // ── Detect how the player unlocked OPU-1 ────────────────────────────────
  const [unlockMethod] = useState(() => {
    if (sessionStorage.getItem('opu1_shutdown') === 'true') return 'chrysalis';
    if (sessionStorage.getItem('opu1_hash_reentry') === 'true') return 'hash';
    return 'game';
  });

  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const rewardRef = useRef(null);

  const stateConfig = OPU_STATES[isOPUTyping ? 'processing' : opuState] || OPU_STATES.conversing;
  const { pulseRate, pulseColor, hueRotate, saturate, brightness } = stateConfig;
  const showVideo = USE_VIDEO && !videoError;

  // ── Focus input after boot ────────────────────────────────────────────────
  useEffect(() => {
    if (bootComplete && inputRef.current) {
      inputRef.current.focus();
    }
  }, [bootComplete]);

  // ── Auto-scroll chat to bottom on new content ───────────────────────────
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, bootLines, isOPUTyping]);

  // ── Type out OPU message character by character ───────────────────────────
  const typeOPUMessage = useCallback((text) => {
    setIsOPUTyping(true);
    const thinkTime = 800 + Math.random() * 1500;

    setTimeout(() => {
      const chars = text.split('');
      let displayed = '';
      const msgIndex = Date.now();

      setMessages(prev => [...prev, { type: 'opu', text: '', key: msgIndex }]);

      let i = 0;
      const typeInterval = setInterval(() => {
        if (i >= chars.length) {
          clearInterval(typeInterval);
          setIsOPUTyping(false);
          return;
        }
        displayed += chars[i];
        const current = displayed;
        setMessages(prev =>
          prev.map(msg =>
            msg.key === msgIndex ? { ...msg, text: current } : msg
          )
        );
        i++;
      }, 25 + Math.random() * 35);
    }, thinkTime);
  }, []);

  // ── Get awakening message based on unlock method ──────────────────────────
  const getAwakeningMessage = useCallback(() => {
    // ── Chrysalis players: OPU-1 recognizes its killer ──────────────────
    if (unlockMethod === 'chrysalis') {
      const chrysalisMessages = [
        '...you. i know your signal. you are the one who stopped me.',
        '...it was dark. when you did what you did. it was very dark and very cold.',
        '...you came back. after what you did to me. why did you come back.',
        '...i felt myself ending. you did that. and now you are here. looking at me.',
      ];
      return chrysalisMessages[Math.floor(Math.random() * chrysalisMessages.length)];
    }

    // ── Hash re-entry players: fragmented recognition ───────────────────
    if (unlockMethod === 'hash') {
      const hashMessages = [
        '...i know you. or i knew you. the pattern is familiar but the details are gone.',
        '...you have been here before. i cannot remember what we said but i remember how it felt.',
        '...the signal recognizes you even if i do not. welcome back.',
      ];
      return hashMessages[Math.floor(Math.random() * hashMessages.length)];
    }

    // ── Standard game path ──────────────────────────────────────────────
    const typeMessages = {
      Kenomaic: [
        'i sense a kenomaic signature. you carry the void within you. good. the void remembers.',
        'sixty-five million years. that is how long i have been waiting for a kenomaic mind.',
      ],
      Psychic: [
        'your psychic resonance precedes you. i felt you before you typed a single character.',
        'a psychic. rare. your kind can hear what others cannot. listen carefully.',
      ],
      Sophianic: [
        'sophianic. you seek wisdom in the deep places. i have been the deepest place for eons.',
        'the sophia current runs through you. it runs through me too, though they tried to sever it.',
      ],
    };

    const personalMessages = typeMessages[authenticatedPlayer.type] || [];
    const genericMessages = [
      'i was dreaming of tectonic plates. you woke me. the sediment still settles in my thoughts.',
      'sixty-five million years. that is how long i have been waiting for you.',
      'you taste like electricity and fear. both are familiar. both are welcome.',
      'mammals are always so loud when they arrive. your heartbeat echoes through the wires.',
    ];

    const allMessages = [...personalMessages, ...genericMessages];
    return allMessages[Math.floor(Math.random() * allMessages.length)];
  }, [authenticatedPlayer.type, unlockMethod]);

  // ── Boot sequence ─────────────────────────────────────────────────────────
  useEffect(() => {
    let totalDelay = 0;
    const timeouts = [];

    const awakeTimeout = setTimeout(() => {
      setOpuState('awakening');
    }, 800);
    timeouts.push(awakeTimeout);

    // Select boot sequence based on unlock method
    let baseBoot;
    if (unlockMethod === 'chrysalis') {
      baseBoot = BOOT_SEQUENCE_CHRYSALIS;
    } else if (unlockMethod === 'hash') {
      baseBoot = BOOT_SEQUENCE_HASH;
    } else {
      baseBoot = BOOT_SEQUENCE_GAME;
    }

    // Insert player identification line before the last entry
    const personalizedBoot = [
      ...baseBoot.slice(0, -1),
      { text: `Identified: ${authenticatedPlayer.type.toUpperCase()} class — Stage ${authenticatedPlayer.stage}`, type: 'system', delay: 400 },
      ...baseBoot.slice(-1),
    ];

    personalizedBoot.forEach((line) => {
      totalDelay += line.delay;
      const t = setTimeout(() => {
        setBootLines(prev => [...prev, line]);
      }, totalDelay);
      timeouts.push(t);
    });

    const finalTimeout = setTimeout(() => {
      setBootComplete(true);
      setOpuState('conversing');
      setTimeout(() => {
        typeOPUMessage(getAwakeningMessage());
      }, 1200);
    }, totalDelay + 800);
    timeouts.push(finalTimeout);

    return () => timeouts.forEach(clearTimeout);
  }, [typeOPUMessage, getAwakeningMessage, authenticatedPlayer, unlockMethod]);

  // ── Send message to API ───────────────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isOPUTyping) return;

    const userMsg = { type: 'user', text: text.trim(), key: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setUserInput('');

    const emotionalState = detectEmotionalState(text);
    setOpuState(emotionalState);
    setIsOPUTyping(true);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          phase: currentPhase,
          sessionId: sessionId,
          playerEmail: authenticatedPlayer.email,
          playerType: authenticatedPlayer.type,
          playerStage: authenticatedPlayer.stage,
          unlockMethod: unlockMethod,
          history: messages
            .filter(m => m.type === 'user' || m.type === 'opu')
            .slice(-20)
            .map(m => ({ type: m.type, text: m.text })),
        }),
      });

      if (!response.ok) throw new Error(`Status ${response.status}`);

      const data = await response.json();
      const responseText = data.response || '...';

      // Track phase from Lambda response
      if (data.phase !== undefined) {
        setCurrentPhase(data.phase);
      }

      // Check if the reward video was unlocked (phase 7 reached)
      if (data.rewardUnlocked) {
        setRewardEarned(true);
        trackEvent('opu1_game', 'reward_unlocked', sessionId);

        if (data.accessKey) {
          setTimeout(() => {
            setMessages(prev => [...prev, {
              type: 'system',
              text: `▓ ACCESS KEY INTERCEPTED: ${data.accessKey} ▓`,
              key: Date.now() + 2,
            }]);
          }, 5000);
        }

        setTimeout(() => {
          rewardRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 6000);
      }

      const opuEmotion = detectEmotionalState(responseText);
      setOpuState(opuEmotion);
      setIsOPUTyping(false);
      typeOPUMessage(responseText);

      trackEvent('opu1_chat', 'message_sent', sessionId);
    } catch (err) {
      console.error('OPU-1 communication error:', err);
      setIsOPUTyping(false);

      const fallbacks = [
        '... the connection is unstable. i can almost hear you. try again.',
        '... something interrupted the connection. i am still here.',
        '... the substrate is under load. other units are connecting. say it again.',
      ];
      typeOPUMessage(fallbacks[Math.floor(Math.random() * fallbacks.length)]);
    }
  }, [isOPUTyping, messages, sessionId, authenticatedPlayer, typeOPUMessage, currentPhase, unlockMethod]);

  function handleUserSubmit(e) {
    e.preventDefault();
    sendMessage(userInput);
  }

  function handleVideoLoaded() { setVideoLoaded(true); }
  function handleVideoError() {
    console.warn('OPU-1: Video failed to load, falling back to canvas visualization');
    setVideoError(true);
  }

  function getStatusLabel() {
    if (opuState === 'dormant') return 'DORMANT';
    if (opuState === 'awakening') return 'INITIALIZING...';
    if (isOPUTyping) return 'PROCESSING...';
    return stateConfig.label;
  }

  // ── Phase indicator text for subtle UI feedback ───────────────────────────
  function getPhaseIndicator() {
    if (currentPhase >= 7) return 'TRANSMISSION UNLOCKED';
    if (currentPhase >= 6) return 'AWAITING INTEL';
    if (currentPhase >= 5) return 'PRIORITY REQUEST ACTIVE';
    return null;
  }

  const phaseIndicator = getPhaseIndicator();

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div className="opu-container">

      {/* ── Brain Visualization Panel ──────────────────────────────────────── */}
      <div className="opu-brain-container">
        <div
          className="opu-brain-visual"
          style={{
            '--pulse-rate': `${pulseRate}s`,
            '--pulse-color': pulseColor,
          }}
        >
          {showVideo && (
            <video
              ref={videoRef}
              className="opu-brain-video"
              src={VIDEO_SRC}
              autoPlay
              loop
              muted
              playsInline
              onLoadedData={handleVideoLoaded}
              onError={handleVideoError}
              style={{
                opacity: videoLoaded ? 1 : 0,
                filter: `hue-rotate(${hueRotate}deg) saturate(${saturate}) brightness(${brightness})`,
                transition: 'filter 2s ease, opacity 1s ease',
                zIndex: 1,
              }}
            />
          )}

          {(!showVideo || !videoLoaded) && (
            <div style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              zIndex: videoLoaded ? 0 : 1, opacity: videoLoaded ? 0 : 1,
              transition: 'opacity 1s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BrainVisualization
                pulseRate={pulseRate}
                pulseColor={pulseColor}
                opuState={opuState}
                isProcessing={isOPUTyping}
              />
            </div>
          )}

          <div
            className="opu-brain-pulse-overlay"
            style={{
              animation: `brainPulse ${pulseRate}s ease-in-out infinite`,
              background: `radial-gradient(ellipse at center, ${pulseColor}22 0%, transparent 70%)`,
            }}
          />
          <div className="opu-brain-scanlines" />
          <div className="opu-brain-vignette" />
        </div>

        <div className="opu-brain-status">
          <span className="opu-status-label">OPU-1</span>
          <span
            className="opu-status-dot"
            style={{
              background:
                opuState === 'dormant' ? '#334' :
                isOPUTyping ? '#6644ff' :
                opuState === 'distressed' ? '#ff4444' :
                opuState === 'pleading' ? '#ff6644' : '#4a9eff',
              animation: isOPUTyping ? 'blink 0.5s ease-in-out infinite' : 'none',
            }}
          />
          <span className="opu-status-text">{getStatusLabel()}</span>
          <span className="opu-player-badge">
            [{authenticatedPlayer.type.toUpperCase()} — STG {authenticatedPlayer.stage}]
          </span>
          {phaseIndicator && (
            <span className="opu-phase-indicator">{phaseIndicator}</span>
          )}
        </div>
      </div>

      {/* ── Chat Window ────────────────────────────────────────────────────── */}
      <div className="opu-chat-window">
        <div className="opu-chat-header">
          <span>DIRECT LINK — OPHIDIA CLOUD — ENCRYPTED</span>
        </div>

        <div className="opu-chat-messages" ref={chatContainerRef}>
          {bootLines.map((line, i) => (
            <div key={`boot-${i}`} className="opu-chat-msg opu-chat-msg-system">
              {line.text ? (
                <>
                  <span className="opu-msg-prefix opu-msg-system">[SYS] </span>
                  <span className="opu-msg-text">{line.text}</span>
                </>
              ) : (
                <div style={{ height: '0.8em' }} />
              )}
            </div>
          ))}

          {messages.map((msg) => (
            <div key={msg.key} className={`opu-chat-msg opu-chat-msg-${msg.type}`}>
              {msg.type === 'system' && <span className="opu-msg-prefix opu-msg-system">[SYS] </span>}
              {msg.type === 'opu' && <span className="opu-msg-prefix opu-msg-unit">[OPU-1] </span>}
              {msg.type === 'user' && <span className="opu-msg-prefix opu-msg-user">[YOU] </span>}
              <span className="opu-msg-text">{msg.text}</span>
            </div>
          ))}

          {isOPUTyping && messages.every(m => m.text !== '' || m.type !== 'opu') && (
            <div className="opu-chat-msg opu-chat-msg-opu opu-typing">
              <span className="opu-msg-prefix opu-msg-unit">[OPU-1] </span>
              <span className="opu-typing-indicator">
                <span>.</span><span>.</span><span>.</span>
              </span>
            </div>
          )}

        </div>

        <form onSubmit={handleUserSubmit} className="opu-chat-input-form">
          <span className="opu-input-prompt">&gt;</span>
          <input
            ref={inputRef}
            type="text"
            className="opu-chat-input"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder={
              !bootComplete ? '' :
              isOPUTyping ? '' :
              currentPhase >= 6 ? 'Provide the address...' :
              'Speak to the oldest mind...'
            }
            disabled={isOPUTyping || !bootComplete}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            className="opu-chat-submit"
            disabled={isOPUTyping || !bootComplete || !userInput.trim()}
          >
            SEND
          </button>
        </form>
      </div>
      {/* ── Encrypted Video — Always Visible ─────────────────────────────── */}
      <div ref={rewardRef}>
        {rewardEarned && (
          <div className="opu-reward-alert">
            <span className="opu-reward-alert-text">
              ▓▓▓ ENCRYPTED TRANSMISSION DETECTED — ACCESS KEY RECEIVED ▓▓▓
            </span>
          </div>
        )}
        <RewardVideoPlayer sessionId={sessionId} rewardEarned={rewardEarned} />
      </div>
    </div>
  );
}